import { useQueueClaimConflict } from '@/hooks/useQueueClaimConflict';
import { Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { QueueRejectionReason, QueueStatus, QueueType } from 'types';

import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { DocumentRejectionModal } from '../DocumentRejectionModal/DocumentRejectionModal';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';

interface GeothermalPermitReviewProps {
  isSaving?: boolean;
}

export const GeothermalPermitReview = ({ isSaving = false }: GeothermalPermitReviewProps) => {
  const { claims, user } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { id = '', action = ReviewPackageActions.review } = useParams<{
    id: string;
    action: ReviewPackageActions;
  }>();
  const { data: permitQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.permit,
  });
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const allowsAction = useIsOrgReadOnly();
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(permitQueue);

  const handleQueueUpdate = async (status: QueueStatus, note?: string, reasons?: QueueRejectionReason[]) => {
    try {
      if (permitQueue?.id) {
        await updateQueue({
          id: permitQueue.id,
          data: {
            assigneeId: user?.sub,
            status,
            ...(reasons && {
              rejectionReasons: [
                {
                  reasons,
                  ...(note && { note }),
                },
              ],
            }),
          },
        }).unwrap();
        toast.success('Queue updated successfully');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(`Error updating queue: ${e.data.message}`);
      } else {
        console.error(e);
        toast.error('Error updating queue');
      }
    }
  };

  const approve = () => checkClaimAndProceed(() => handleQueueUpdate(QueueStatus.approved));

  const handleRejectClick = () => checkClaimAndProceed(openModal);

  const handleRejectModal = async (values: { rejectionReason: string; notes: string | undefined }) => {
    const { rejectionReason, notes } = values;
    await handleQueueUpdate(QueueStatus.rejected, notes, [rejectionReason as QueueRejectionReason]);
    closeModal();
  };

  if (!permitQueue || (permitQueue.status !== QueueStatus.rejected && !isFinCoUser)) {
    return null;
  }

  return (
    <>
      {claimConflictModal}
      <DocumentRejectionModal
        isModalOpen={isModalOpen}
        title="Geothermal Permit"
        handleClose={closeModal}
        handleSubmit={handleRejectModal}
      />
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.permit} />
      <Box padding="md" margin="0 0 lg 0" borderColor="separator" borderWidth="xs" gap="sm">
        <h3>Geothermal Permit Review</h3>
        <QueueBanner
          setIsNotesOpen={setIsNotesOpen}
          queueType={QueueType.permit}
          isFinCoUser={isFinCoUser}
          hideFlagCount
        />
        {action === ReviewPackageActions.review && permitQueue.status !== QueueStatus.approved && (
          <Box direction="row" childGap="xs" justifyContent="flex-end" alignItems="flex-end">
            <Button
              size="sm"
              variant="primary"
              isLoading={isUpdatingQueue}
              onClick={approve}
              disabled={!allowsAction || isSaving}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="primary"
              tone="danger"
              isLoading={isUpdatingQueue}
              onClick={handleRejectClick}
              disabled={!allowsAction || isSaving}
            >
              Reject
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
